import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  handleTextInput,
  handleTimeInput,
  validateText,
  validateTime,
  getCharacterCount,
  FIELD_LIMITS
} from '../../utils/inputValidation';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Edit,
  Upload,
  ChevronLeft,
  ChevronRight,
  Home,
  Zap,
  Award,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Send,
  Download,
  Eye,
  ArrowLeft,
  Timer,
  User,
  FileText,
  X,
  LogOut,
  TrendingUp
} from 'lucide-react';
import Button from '../../components/common/Button/Button';
import ExportModal from '../../components/common/ExportModal.jsx';
import SelectedExportFormatBar from '../../components/common/SelectedExportFormatBar';
import essService from '../../api/services/essService';
import { regularizationReasonService } from "../../api/crm/config/configService";
import { toast } from '../../utils/toast';
import HolidayCalendar from '../hrms/attendance-leaves/HolidayCalendar';
import { formatDate } from '../../utils/stringUtils';
import PaginationControls from '../../components/common/Pagination/PaginationControls';
import RegularizationRequestModal from '../../components/hrms/RegularizationRequestModal';

const formatTimeAMPM = (timeStr) => {
  if (!timeStr || timeStr === 'N/A') return 'N/A';
  // Handle if timeStr already has AM/PM
  if (timeStr.includes('AM') || timeStr.includes('PM')) return timeStr;

  if (String(timeStr).includes('T')) {
    const parsed = new Date(timeStr);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }

  try {
    const [hours, minutes] = timeStr.split(':');
    if (!hours || !minutes) return timeStr;

    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);

    if (isNaN(h) || isNaN(m)) return timeStr;

    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const parseWorkingHoursToMinutes = (hoursText) => {
  if (!hoursText || hoursText === 'N/A') return 0;
  const normalized = String(hoursText).trim().toLowerCase();

  // Supports "8.50h"
  const decimalMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*h$/);
  if (decimalMatch) {
    const hrs = Number.parseFloat(decimalMatch[1]);
    return Number.isNaN(hrs) ? 0 : Math.max(0, Math.round(hrs * 60));
  }

  // Supports "8h 30m"
  const hmMatch = normalized.match(/^(\d+)\s*h\s*(\d+)\s*m$/);
  if (hmMatch) {
    return (Number.parseInt(hmMatch[1], 10) * 60) + Number.parseInt(hmMatch[2], 10);
  }

  return 0;
};

const parseWorkingHoursToDecimal = (hoursText) => {
  const mins = parseWorkingHoursToMinutes(hoursText);
  return mins > 0 ? Number((mins / 60).toFixed(2)) : 0;
};

const MyAttendance = () => {
  const [activeTab, setActiveTab] = useState('attendance');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayView, setShowDayView] = useState(false);
  const [showRegularizationModal, setShowRegularizationModal] = useState(false);
  const [showNewRegModal, setShowNewRegModal] = useState(false);
  const [regModalPrefilledData, setRegModalPrefilledData] = useState(null);
  const [selectedRegRequest, setSelectedRegRequest] = useState(null);
  const [showCompOffModal, setShowCompOffModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // --- Selection & Export States ---
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Clear filters
  const clearFilters = () => {
    setFilterStatus('all');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPageSize(limit);
    setCurrentPage(1);
  };

  // Attendance data for today (for clock in/out)
  const [attendanceData, setAttendanceData] = useState({
    today: {
      date: new Date().toISOString().split('T')[0],
      status: null,
      hours: 0,
      checkIn: null,
      checkOut: null,
      anomalies: [],
      punches: [],
      sessions: [] // Store all sessions for today
    }
  });

  const [selectedStatsMonth, setSelectedStatsMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [monthlyStats, setMonthlyStats] = useState({
    averageWorkedMinutes: 0,
    punctualityPercent: 0,
    presentDays: 0,
    totalDays: 0,
    loading: false
  });

  const [regularizationRequests, setRegularizationRequests] = useState([]);
  const [regularizationPagination, setRegularizationPagination] = useState({
    totalItems: 0,
    totalPages: 1
  });
  const [regularizationPage, setRegularizationPage] = useState(1);
  const [regularizationPageSize, setRegularizationPageSize] = useState(10);

  const [compOffData, setCompOffData] = useState({
    available: 0,
    used: 0,
    pending: 0,
    history: []
  });
  const [rosterPagination, setRosterPagination] = useState({
    totalItems: 0,
    totalPages: 1
  });
  const [compOffPage, setCompOffPage] = useState(1);
  const [compOffPageSize, setCompOffPageSize] = useState(10);

  const [overtimeData, setOvertimeData] = useState({
    total: 0,
    used: 0,
    pending: 0,
    history: []
  });
  const [overtimePage, setOvertimePage] = useState(1);
  const [overtimePageSize, setOvertimePageSize] = useState(10);

  const [holidays, setHolidays] = useState([]);
  const [wfhToday, setWfhToday] = useState({
    approvedToday: false,
    requestId: null,
    workLocation: ''
  });
  const [wfhPunchLoading, setWfhPunchLoading] = useState(false);

  const [shiftData, setShiftData] = useState({
    current: {
      name: null,
      startTime: null,
      endTime: null,
      breakDuration: 0,
      workingDays: []
    },
    roster: []
  });
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterPageSize, setRosterPageSize] = useState(10);

  const [regularizationForm, setRegularizationForm] = useState({
    date: '',
    checkIn: '',
    checkOut: '',
    reason: '',
    reasonId: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const inFlightAttendanceRequestRef = useRef(null);
  const inFlightTodayRequestRef = useRef(null);
  const attendanceAbortControllerRef = useRef(null);
  const todayAbortControllerRef = useRef(null);

  const closeAttendanceDetails = () => {
    setShowAttendanceDetails(false);
    setSelectedAttendanceRecord(null);
    setActiveTab('attendance');
  };

  useEffect(() => {
    const handlePopState = () => {
      if (showAttendanceDetails) {
        closeAttendanceDetails();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showAttendanceDetails]);

  // Fetch regularization reasons for the form
  const { data: reasonsData } = useQuery({
    queryKey: ["regularizationReasons", "dropdown"],
    queryFn: () => regularizationReasonService.getDropdown()
  });
  const dynamicReasons = reasonsData?.data || [];

  // Use React Query to cache employee profile (shared across all functions)
  const {
    data: employeeProfile,
    isLoading: profileLoading
  } = useQuery({
    queryKey: ['employeeProfile'],
    queryFn: async () => {
      try {
        const result = await essService.getProfile();
        if (result.success && result.data) {
          return result.data;
        }
        return null;
      } catch (error) {

        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Set employee ID from cached profile
  useEffect(() => {
    if (employeeProfile) {
      // Use the string employeeId (e.g., "EMP001") for searching/filtering
      // Fallback to _id if employeeId string is not present
      setEmployeeId(employeeProfile.employeeId || employeeProfile._id || employeeProfile.id);
    }
  }, [employeeProfile]);

  // Initial loading state control
  useEffect(() => {
    if (employeeProfile && !profileLoading) {
      setInitialLoading(false);
    }
  }, [employeeProfile, profileLoading]);

  // Load Today's Attendance summary when tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'attendance') {
      loadTodayAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab]);

  // Load attendance logs when attendance tab is active and filters/page change

  useEffect(() => {
    if (employeeId && activeTab === "attendance") {
      loadMonthlyStats();
    }
  }, [employeeId, selectedStatsMonth, activeTab]);
  useEffect(() => {
    if (employeeId && activeTab === 'attendance') {
      loadAttendanceLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab, currentPage, itemsPerPage, filterStatus, fromDate, toDate]);

  // Load regularization requests when regularization tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'regularization') {
      loadRegularizationRequests(regularizationPage, regularizationPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab, regularizationPage, regularizationPageSize]);

  // Load shift and roster when shift tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'shift') {
      loadShiftAndRoster(rosterPage, rosterPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab, rosterPage, rosterPageSize]);

  // Load holidays when holidays tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'holidays') {
      loadHolidays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab]);

  // --- Selection & Export Logic ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = attendanceLogs.map(log => log.id || log.logId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    if (!id) return;
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const currentPageLogIds = useMemo(
    () =>
      attendanceLogs
        .map((log) => {
          const raw = log?.id ?? log?.logId;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [attendanceLogs]
  );

  // Helper to format ordinal number with suffix (1 -> 1st Grace, 2 -> 2nd Grace, 3 -> 3rd Grace, etc.)
  const getOrdinalGraceLabel = (num) => {
    if (!num) return '';
    const j = num % 10;
    const k = num % 100;
    let suffix = 'th';
    if (j === 1 && k !== 11) suffix = 'st';
    else if (j === 2 && k !== 12) suffix = 'nd';
    else if (j === 3 && k !== 13) suffix = 'rd';
    return `${num}${suffix} Grace`;
  };

  // Determine which late attendance days qualify for the dynamic grace days per month allowance
  const { freeLateLogIds, freeLateDates, freeLateIndexMap } = useMemo(() => {
    const byMonth = {};
    const sorted = [...attendanceLogs].sort((a, b) => {
      const dateA = a?.date ? new Date(a.date).getTime() : 0;
      const dateB = b?.date ? new Date(b.date).getTime() : 0;
      return dateA - dateB;
    });

    sorted.forEach((log) => {
      if (!log?.checkinLate) return;
      const month = String(log.date || '').slice(0, 7); // YYYY-MM
      if (!month) return;
      if (!byMonth[month]) byMonth[month] = [];

      // Determine allowed grace days dynamically from shift details (fallback to shiftData or 0)
      const allowedGraceDays = Number(
        log?.shiftDetails?.graceDays ??
        log?.shift?.graceDays ??
        log?.rosterDetails?.shiftId?.graceDays ??
        log?.graceDays ??
        shiftData?.current?.graceDays ??
        0
      );

      // Determine allowed grace days timing dynamically from shift details (fallback to shiftData or 0)
      const allowedGraceDaysTime = Number(
        log?.shiftDetails?.graceDaysTime ??
        log?.shift?.graceDaysTime ??
        log?.rosterDetails?.shiftId?.graceDaysTime ??
        log?.graceDaysTime ??
        shiftData?.current?.graceDaysTime ??
        0
      );

      // If graceDaysTime is configured (> 0), verify the checkin late duration does not exceed it
      let isWithinGraceDaysTiming = true;
      if (allowedGraceDaysTime > 0 && log.checkinLateTime) {
        const parts = String(log.checkinLateTime).split(':').map(Number);
        let lateMins = 0;
        if (parts.length === 3) {
          lateMins = (parts[0] * 60) + parts[1] + (parts[2] / 60);
        } else if (parts.length === 2) {
          lateMins = parts[0] + (parts[1] / 60);
        } else if (!isNaN(Number(log.checkinLateTime))) {
          lateMins = Number(log.checkinLateTime);
        }
        if (lateMins > allowedGraceDaysTime) {
          isWithinGraceDaysTiming = false;
        }
      }

      if (allowedGraceDays > 0 && isWithinGraceDaysTiming && byMonth[month].length < allowedGraceDays) {
        byMonth[month].push(log);
      }
    });

    const freeIds = new Set();
    const freeDates = new Set();
    const indexMap = new Map();
    Object.values(byMonth).forEach((logs) => {
      logs.forEach((l, idx) => {
        const lateNum = idx + 1; // 1 for 1st Grace, 2 for 2nd Grace, etc.
        if (l.id) {
          freeIds.add(String(l.id));
          indexMap.set(String(l.id), lateNum);
        }
        if (l.logId) {
          freeIds.add(String(l.logId));
          indexMap.set(String(l.logId), lateNum);
        }
        if (l._id) {
          freeIds.add(String(l._id));
          indexMap.set(String(l._id), lateNum);
        }
        if (l.date) {
          freeDates.add(String(l.date));
          indexMap.set(String(l.date), lateNum);
        }
      });
    });

    return { freeLateLogIds: freeIds, freeLateDates: freeDates, freeLateIndexMap: indexMap };
  }, [attendanceLogs, shiftData]);

  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      status: filterStatus !== 'all' ? filterStatus : undefined,
      startDate: fromDate || undefined,
      endDate: toDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [filterStatus, fromDate, toDate]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageLogIds)
          : [];

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'my-attendance',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: 'date',
            sortOrder: 'desc',
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `my-attendance-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, currentPage, itemsPerPage, currentPageLogIds]
  );

  const handleMyAttendanceSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected records to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} record(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleMyAttendanceScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const totalFiltered = Number(pagination?.totalLogs ?? 0);

        if (scope === 'currentPage' && !attendanceLogs.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No attendance records match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: filterStatus !== 'all' ? { status: filterStatus } : {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageLogIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      attendanceLogs.length,
      pagination?.totalLogs,
      baseExportFilters,
      filterStatus,
      requestBackendExport,
      itemsPerPage,
      currentPageLogIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Load attendance logs from HRMS API (same as HRMS attendance logs)
  const loadAttendanceLogs = async () => {
    if (!employeeId) return;

    const attendanceRequestKey = JSON.stringify({
      employeeId,
      currentPage,
      itemsPerPage,
      filterStatus,
      fromDate,
      toDate
    });

    // Prevent duplicate in-flight calls for identical params (common during rapid rerenders/StrictMode)
    if (inFlightAttendanceRequestRef.current === attendanceRequestKey) {
      return;
    }

    try {
      inFlightAttendanceRequestRef.current = attendanceRequestKey;
      setLoading(true);
      if (attendanceAbortControllerRef.current) {
        attendanceAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      attendanceAbortControllerRef.current = controller;
      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", itemsPerPage.toString());
      // The backend expects 'search' for filtering by ID/Name in biometric-attendance
      queryParams.append("search", employeeId);
      if (filterStatus !== "all") queryParams.append("status", filterStatus);
      if (fromDate) queryParams.append("startDate", fromDate);
      if (toDate) queryParams.append("endDate", toDate);

      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;


      const token = localStorage.getItem("auth_token");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();


      if (data.responseCode === 200) {
        const logs = data.data.logs || [];
        const rawPagination = data.data.pagination || {
          currentPage,
          totalPages: 1,
          totalLogs: logs.length,
          hasNextPage: false,
          hasPrevPage: false,
          limit: itemsPerPage
        };

        const normalizedPagination = {
          currentPage: rawPagination.currentPage || currentPage,
          totalPages: Math.max(rawPagination.totalPages || 1, 1),
          totalLogs: rawPagination.totalLogs ?? logs.length,
          hasNextPage: rawPagination.hasNextPage ?? false,
          hasPrevPage: rawPagination.hasPrevPage ?? false,
          limit: rawPagination.limit ?? itemsPerPage
        };

        setAttendanceLogs(logs);
        setPagination(normalizedPagination);

        if (data.data && data.data.wfhToday) {
          setWfhToday(data.data.wfhToday);
        }

        if (normalizedPagination.totalPages > 0 && currentPage > normalizedPagination.totalPages) {
          setCurrentPage(normalizedPagination.totalPages);
        }
      } else {
        throw new Error(data.responseMessage || 'Failed to load attendance logs');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      toast.error('Failed to load attendance logs');
      setAttendanceLogs([]);
    } finally {
      if (inFlightAttendanceRequestRef.current === attendanceRequestKey) {
        inFlightAttendanceRequestRef.current = null;
      }
      if (attendanceAbortControllerRef.current) {
        attendanceAbortControllerRef.current = null;
      }
      setLoading(false);
    }
  };

  // Load today's attendance using biometric-attendance API
  const loadTodayAttendance = async () => {
    if (!employeeId) return;

    const todayRequestKey = `${employeeId}-${new Date().toISOString().split('T')[0]}`;
    if (inFlightTodayRequestRef.current === todayRequestKey) {
      return;
    }

    try {
      inFlightTodayRequestRef.current = todayRequestKey;
      if (todayAbortControllerRef.current) {
        todayAbortControllerRef.current.abort();
      }

      const controller = new AbortController();
      todayAbortControllerRef.current = controller;
      const today = new Date().toISOString().split('T')[0];

      // Make both API calls in parallel, executing state updates immediately as they resolve
      const biometricPromise = (async () => {
        const queryParams = new URLSearchParams();
        queryParams.append("search", employeeId);
        queryParams.append("startDate", today);
        queryParams.append("endDate", today);
        queryParams.append("limit", "1");

        const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;
        const token = localStorage.getItem("auth_token");

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          signal: controller.signal
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.responseCode === 200) {
            // Set WFH status immediately if available in biometric response
            if (data.data && data.data.wfhToday) {
              setWfhToday(data.data.wfhToday);
            }

            if (data.data.logs && data.data.logs.length > 0) {
              const todayLog = data.data.logs[0];
              setAttendanceData(prev => ({
                ...prev,
                today: {
                  date: today,
                  status: todayLog.status || 'Present',
                  hours: parseWorkingHoursToDecimal(todayLog.workingHours),
                  checkIn: todayLog.checkIn !== 'N/A' ? todayLog.checkIn : null,
                  checkOut: todayLog.checkOut !== 'N/A' ? todayLog.checkOut : null,
                  anomalies: [],
                  punches: todayLog.checkInPunches || [],
                  sessions: prev.today.sessions || []
                }
              }));
            } else {
              setAttendanceData(prev => ({
                ...prev,
                today: {
                  date: today,
                  status: 'Absent',
                  hours: 0,
                  checkIn: null,
                  checkOut: null,
                  anomalies: [],
                  punches: [],
                  sessions: prev.today.sessions || []
                }
              }));
            }
          }
        }
      })();

      const wfhPromise = (async () => {
        const todayResult = await essService.getTodayAttendance();
        if (todayResult && todayResult.success) {
          const wfh = todayResult.data?.wfh || {};
          const record = todayResult.data?.record || null;
          
          setWfhToday({
            approvedToday: Boolean(wfh.approvedToday),
            requestId: wfh.requestId || null,
            workLocation: wfh.workLocation || ''
          });

          if (record) {
            setAttendanceData(prev => ({
              ...prev,
              today: {
                ...prev.today,
                date: today,
                status: record.status || prev.today.status,
                hours: typeof record.workingHours === 'number'
                  ? Number((record.workingHours / 60).toFixed(2))
                  : prev.today.hours,
                checkIn: record.checkIn || prev.today.checkIn,
                checkOut: record.checkOut || prev.today.checkOut,
                sessions: record.allSessions || prev.today.sessions || []
              }
                }));
              }
            }
      })();

      // Wait for both promises to finish in parallel, though their state updates ran already
      await Promise.allSettled([biometricPromise, wfhPromise]);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Error loading today attendance:', error);
    } finally {
      if (inFlightTodayRequestRef.current === todayRequestKey) {
        inFlightTodayRequestRef.current = null;
      }
      if (todayAbortControllerRef.current) {
        todayAbortControllerRef.current = null;
      }
    }
  };

  async function loadMonthlyStats() {
    if (!employeeId || !selectedStatsMonth) return;

    setMonthlyStats(prev => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem("auth_token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/stats/employee?employeeId=${employeeId}&month=${selectedStatsMonth}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          setMonthlyStats({
            ...data.data,
            loading: false
          });
        }
      }
    } catch (error) {
      setMonthlyStats(prev => ({ ...prev, loading: false }));
    }
  }

  const handleWfhClockIn = async () => {
    if (!wfhToday.approvedToday || wfhPunchLoading) return;
    setWfhPunchLoading(true);
    try {
      const result = await essService.clockIn(
        {
          type: 'remote',
          address: wfhToday.workLocation || 'Work From Home'
        },
        `WFH check-in${wfhToday.requestId ? ` (${wfhToday.requestId})` : ''}`
      );
      if (result.success) {
        toast.success('WFH check-in successful');
        await loadTodayAttendance();
        await loadAttendanceLogs();
        await loadMonthlyStats();
      } else {
        toast.error(result.error || 'Failed to check in');
      }
    } catch (error) {
      toast.error('Failed to check in');
    } finally {
      setWfhPunchLoading(false);
    }
  };

  const handleWfhClockOut = async () => {
    if (!wfhToday.approvedToday || wfhPunchLoading) return;
    setWfhPunchLoading(true);
    try {
      const result = await essService.clockOut(
        `WFH check-out${wfhToday.requestId ? ` (${wfhToday.requestId})` : ''}`,
        {
          type: 'remote',
          address: wfhToday.workLocation || 'Work From Home'
        },
        { type: 'web_portal' }
      );
      if (result.success) {
        toast.success('WFH check-out successful');
        await loadTodayAttendance();
        await loadAttendanceLogs();
        await loadMonthlyStats();
      } else {
        toast.error(result.error || 'Failed to check out');
      }
    } catch (error) {
      toast.error('Failed to check out');
    } finally {
      setWfhPunchLoading(false);
    }
  };

  // Load holidays
  const loadHolidays = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const result = await essService.getHolidays(year, month);

      if (result.success && result.data) {
        // Handle both array response and wrapped response
        const holidaysArray = Array.isArray(result.data) ? result.data : (result.data.holidays || []);
        const transformedHolidays = holidaysArray.map(holiday => ({
          date: holiday.date,
          name: holiday.name,
          type: holiday.type || 'national'
        }));
        setHolidays(transformedHolidays);
      } else {
        // If API fails, set empty array instead of fallback data
        setHolidays([]);
      }
    } catch (error) {

      // Set empty array on error - no fallback data
      setHolidays([]);
    }
  };

  // Load regularization requests
  const loadRegularizationRequests = async (page = regularizationPage, limit = regularizationPageSize) => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setRegularizationRequests([]);
        setRegularizationPagination({
          totalItems: 0,
          totalPages: 1
        });
        return;
      }

      // Use cached employee profile from React Query
      if (!employeeProfile) {

        setRegularizationRequests([]);
        return;
      }

      const employeeId = employeeProfile._id || employeeProfile.id;

      if (!employeeId) {

        setRegularizationRequests([]);
        return;
      }

      // Fetch regularization requests for this employee
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', Math.min(limit, 10).toString());
      params.append('employeeId', employeeId);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization-test?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.responseCode === 200) {
          const payload = data.data || data;
          const requestsSource = Array.isArray(payload) ? payload : (payload.requests || payload.data?.requests || []);
          const requests = Array.isArray(requestsSource) ? requestsSource : [];
          const pagination = payload.pagination || payload.data?.pagination || {};

          // Helper function to extract time from Date or time string
          // This matches the backend's toTimeString().split(' ')[0] format
          const extractTime = (timeValue) => {
            if (!timeValue) return '';
            // If it's already a time string (HH:MM or HH:MM:SS), return first 5 chars
            if (typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}/)) {
              return timeValue.substring(0, 5);
            }
            // If it's a Date object or ISO string, extract time using toTimeString() method
            // This matches how the backend formats times in approve/reject responses
            try {
              const date = new Date(timeValue);
              if (!isNaN(date.getTime())) {
                // Use toTimeString() to get HH:MM:SS format, then take first 5 chars for HH:MM
                // This gives us the time in the browser's local timezone, which should match
                // the server's local timezone if they're the same, or we need to handle timezone
                const timeString = date.toTimeString().split(' ')[0]; // Gets "HH:MM:SS"
                return timeString.substring(0, 5); // Returns "HH:MM"
              }
            } catch (e) {

            }
            return '';
          };

          // Transform requests to match frontend structure
          const transformedRequests = requests.map(request => ({
            id: request._id || request.requestId,
            requestId: request.requestId,
            date: request.date ? new Date(request.date).toISOString().split('T')[0] : '',
            checkIn: extractTime(request.requestedCheckIn),
            checkOut: extractTime(request.requestedCheckOut),
            reason: request.reason || '',
            description: request.description || '',
            status: request.status || 'pending',
            submittedAt: request.submittedDate || request.createdAt,
            approvedBy: request.approvedBy ? (
              request.approvedBy.personalInfo
                ? `${request.approvedBy.personalInfo.firstName || ''} ${request.approvedBy.personalInfo.lastName || ''}`.trim()
                : (request.approvedBy.firstName && request.approvedBy.lastName
                  ? `${request.approvedBy.firstName} ${request.approvedBy.lastName}`.trim()
                  : request.approvedByName || null)
            ) : (request.approvedByName || null),
            approvedDate: request.approvedDate,
            rejectionReason: request.rejectionReason,
            comments: request.comments
          }));

          setRegularizationRequests(transformedRequests);
          const resolvedTotalItems = pagination.totalItems ?? requests.length;
          const resolvedTotalPages = pagination.totalPages ?? Math.max(1, Math.ceil((resolvedTotalItems || 0) / limit));
          if (resolvedTotalPages > 0 && page > resolvedTotalPages) {
            setRegularizationPage(resolvedTotalPages);
          }
          setRegularizationPagination({
            totalItems: resolvedTotalItems,
            totalPages: resolvedTotalPages
          });
        } else {

          setRegularizationRequests([]);
          setRegularizationPagination({
            totalItems: 0,
            totalPages: 1
          });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));

        setRegularizationRequests([]);
        setRegularizationPagination({
          totalItems: 0,
          totalPages: 1
        });
      }
    } catch (error) {

      setRegularizationRequests([]);
      setRegularizationPagination({
        totalItems: 0,
        totalPages: 1
      });
    }
  };

  // Status helper functions (matching HRMS attendance logs format)
  const getStatusColor = (status) => {
    // UI badge tokens (success/warning/error/neutral) — keep logic intact
    if (!status) return 'bg-[#F9FAFB] text-[#6B7280] border border-[#E5E7EB]';
    const statusLower = String(status).toLowerCase().trim();

    const green = 'bg-[#16A34A]/10 text-[#16A34A] border border-[#E5E7EB]';
    const orange = 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#E5E7EB]';
    const red = 'bg-[#DC2626]/10 text-[#DC2626] border border-[#E5E7EB]';
    const neutral = 'bg-[#F9FAFB] text-[#6B7280] border border-[#E5E7EB]';

    switch (statusLower) {
      // On Time / Present
      case 'present':
      case 'on_time':
      case 'on time':
        return green;

      // Late / Half Day
      case 'late':
      case 'half_day':
      case 'half day':
        return orange;

      // Missing / No checkout / Absent
      case 'absent':
      case 'missing':
      case 'no_checkout':
      case 'no checkout':
      case 'missing_checkout':
      case 'missing check out':
      case 'missing check-out':
        return red;

      case 'holiday':
      case 'wfh':
      case 'od':
        return neutral;

      case 'pending':
        return orange;
      case 'approved':
        return green;
      case 'rejected':
        return red;

      default:
        return neutral;
    }
  };

  // Load shift and roster data for current employee
  const loadShiftAndRoster = async (page = rosterPage, limit = rosterPageSize) => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setShiftData({
          current: {
            name: null,
            startTime: null,
            endTime: null,
            breakDuration: 0,
            workingDays: []
          },
          roster: []
        });
        setRosterPagination({
          totalItems: 0,
          totalPages: 1
        });
        return;
      }

      // Try to get employee ID from user data in localStorage first
      let employeeId = null;
      try {
        const userData = localStorage.getItem('user_data');
        if (userData) {
          const user = JSON.parse(userData);
          // Check if user has employeeId directly
          if (user.employeeId) {
            employeeId = user.employeeId;
          } else if (user.employee && user.employee._id) {
            employeeId = user.employee._id;
          }
        }
      } catch (e) {

      }

      // If not found in localStorage, try to get from backend using a simple endpoint
      // that doesn't require self_service permission
      if (!employeeId) {
        try {
          // Try to get employee by user ID from a simple endpoint
          const userResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/roster-test/current-user`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              }
            }
          );

          if (userResponse.ok) {
            const userData = await userResponse.json();
            employeeId = userData.data?.employeeId || userData.employeeId;
          }
        } catch (e) {

        }
      }

      // Use cached employee profile from React Query
      // Always use MongoDB _id for roster queries (not employeeId string)
      if (!employeeId && employeeProfile) {
        employeeId = employeeProfile._id || employeeProfile.id;
      } else if (employeeId && !employeeId.match(/^[0-9a-fA-F]{24}$/) && employeeProfile) {
        // If employeeId is a string like "EMP001", get the MongoDB _id from profile
        employeeId = employeeProfile._id || employeeProfile.id;
      }

      if (!employeeId) {

        setShiftData({
          current: {
            name: null,
            startTime: null,
            endTime: null,
            breakDuration: 0,
            workingDays: []
          },
          roster: []
        });
        setRosterPagination({
          totalItems: 0,
          totalPages: 1
        });
        return;
      }



      // Store base shift from employee profile (this should never be modified by rosters)
      let baseShift = null;
      if (employeeProfile && employeeProfile.employmentInfo && employeeProfile.employmentInfo.shift) {
        baseShift = employeeProfile.employmentInfo.shift;
      }

      // Fetch roster assignments for this employee
      const params = new URLSearchParams();
      params.append('employeeId', employeeId);
      params.append('page', page.toString());
      params.append('limit', Math.min(limit, 10).toString());

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/roster-test?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const rostersPayload = data.data?.rosters ?? data.data;
        const rosters = Array.isArray(rostersPayload) ? rostersPayload : [];
        const pagination = data.data?.pagination || data.pagination || {};




        // Check for active roster that overrides base shift for today
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let activeRoster = null;
        for (const roster of rosters) {
          const startDate = new Date(roster.startDate);
          const endDate = new Date(roster.endDate);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);

          // Check if today falls within this roster's date range
          if (today >= startDate && today <= endDate) {
            activeRoster = roster;
            break; // Use the first active roster found
          }
        }

        // Determine current shift: roster override takes priority, then base shift
        let currentShift = null;
        if (activeRoster && activeRoster.shiftId) {
          // Use roster shift (override)
          let shiftDetails = activeRoster.shiftId;

          // If shiftId is an object but doesn't have full details, fetch it
          if (typeof shiftDetails === 'string' || (typeof shiftDetails === 'object' && !shiftDetails.name)) {
            const shiftIdToFetch = typeof shiftDetails === 'string' ? shiftDetails : (shiftDetails._id || shiftDetails.id);

            if (shiftIdToFetch) {
              try {
                const shiftResponse = await fetch(
                  `${process.env.REACT_APP_API_BASE_URL}/shifts/${shiftIdToFetch}`,
                  {
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json"
                    }
                  }
                );
                if (shiftResponse.ok) {
                  const shiftData = await shiftResponse.json();
                  shiftDetails = shiftData.data?.shift || shiftData.data || shiftDetails;
                }
              } catch (error) {

              }
            }
          }

          if (shiftDetails && shiftDetails.name) {
            currentShift = {
              name: shiftDetails.name || 'N/A',
              startTime: shiftDetails.startTime || null,
              endTime: shiftDetails.endTime || null,
              breakDuration: shiftDetails.breakDuration || 0,
              workingDays: shiftDetails.workingDays || [],
              isRosterOverride: true, // Flag to indicate this is from roster
              rosterDateRange: `${formatDate(activeRoster.startDate, { includeTime: false })} - ${formatDate(activeRoster.endDate, { includeTime: false })}`,
              graceCheckInTime: Number(shiftDetails.graceCheckInTime || 0),
              graceDays: Number(shiftDetails.graceDays || 0),
              graceDaysTime: Number(shiftDetails.graceDaysTime || shiftDetails.graceDaysTiming || 0)
            };
          }
        }

        // If no active roster, use base shift
        if (!currentShift && baseShift) {
          currentShift = {
            name: baseShift.name || 'No shift assigned',
            startTime: baseShift.startTime || null,
            endTime: baseShift.endTime || null,
            breakDuration: baseShift.breakDuration || 0,
            workingDays: baseShift.workingDays || [],
            isRosterOverride: false,
            graceCheckInTime: Number(baseShift.graceCheckInTime || 0),
            graceDays: Number(baseShift.graceDays || 0),
            graceDaysTime: Number(baseShift.graceDaysTime || baseShift.graceDaysTiming || 0)
          };
        } else if (!currentShift) {
          // No shift at all
          currentShift = {
            name: null,
            startTime: null,
            endTime: null,
            breakDuration: 0,
            workingDays: []
          };
        }

        // Set current shift
        setShiftData(prev => ({
          ...prev,
          current: currentShift
        }));

        // Transform roster data for display
        const transformedRoster = rosters.map(roster => {
          let shiftName = 'N/A';
          let startTime = null;
          let endTime = null;
          let breakDuration = 0;

          if (roster.shiftId) {
            if (typeof roster.shiftId === 'object' && roster.shiftId.name) {
              shiftName = roster.shiftId.name;
              startTime = roster.shiftId.startTime;
              endTime = roster.shiftId.endTime;
              breakDuration = roster.shiftId.breakDuration || 0;
            } else if (typeof roster.shiftId === 'string') {
              shiftName = 'Loading...';
            }
          }

          return {
            date: roster.startDate,
            endDate: roster.endDate,
            shift: shiftName,
            startTime: startTime || 'N/A',
            endTime: endTime || 'N/A',
            breakDuration: breakDuration,
            status: roster.status || 'Upcoming'
          };
        });

        setShiftData(prev => ({
          ...prev,
          roster: transformedRoster
        }));
        const resolvedRosterTotal = pagination.totalItems ?? rosters.length;
        const resolvedRosterPages = pagination.totalPages ?? Math.max(1, Math.ceil((resolvedRosterTotal || 0) / limit));
        if (resolvedRosterPages > 0 && page > resolvedRosterPages) {
          setRosterPage(resolvedRosterPages);
        }
        setRosterPagination({
          totalItems: resolvedRosterTotal,
          totalPages: resolvedRosterPages
        });
      } else {

        setShiftData({
          current: {
            name: null,
            startTime: null,
            endTime: null,
            breakDuration: 0,
            workingDays: []
          },
          roster: []
        });
        setRosterPagination({
          totalItems: 0,
          totalPages: 1
        });
      }
    } catch (error) {

      setShiftData({
        current: {
          name: null,
          startTime: null,
          endTime: null,
          breakDuration: 0,
          workingDays: []
        },
        roster: []
      });
      setRosterPagination({
        totalItems: 0,
        totalPages: 1
      });
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return <Clock className="h-4 w-4 text-gray-500" />;
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'present':
      case 'on_time':
      case 'on time':
        return <CheckCircle className="h-4 w-4 text-[#16A34A]" />;
      case 'late':
      case 'half_day':
      case 'half day':
        return <AlertCircle className="h-4 w-4 text-[#F59E0B]" />;
      case 'absent':
      case 'missing':
      case 'no_checkout':
      case 'no checkout':
      case 'missing_checkout':
      case 'missing check out':
        return <XCircle className="h-4 w-4 text-[#DC2626]" />;
      case 'wfh': return <Home className="h-4 w-4 text-blue-500" />;
      case 'od': return <MapPin className="h-4 w-4 text-purple-500" />;
      case 'holiday': return <CalendarIcon className="h-4 w-4 text-[#F59E0B]" />;
      case 'pending': return <Clock className="h-4 w-4 text-[#F59E0B]" />;
      case 'approved': return <CheckCircle className="h-4 w-4 text-[#16A34A]" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-[#DC2626]" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getAnomalyIcon = (anomaly) => {
    switch (anomaly) {
      case 'late': return <AlertCircle className="w-3 h-3 text-yellow-600" />;
      case 'early': return <Clock className="w-3 h-3 text-[#0F2942]" />;
      case 'overtime': return <Zap className="w-3 h-3 text-purple-600" />;
      case 'missed': return <XCircle className="w-3 h-3 text-red-600" />;
      default: return <AlertCircle className="w-3 h-3 text-gray-600" />;
    }
  };
  const parseHoursTextToMinutes = (hoursText) => {
    return parseWorkingHoursToMinutes(hoursText);
  };

  const getEffectiveHoursForLog = (log) => {
    const explicit = log?.effectiveHours || log?.workingHours;
    if (explicit && explicit !== 'N/A') return String(explicit).includes('h') ? explicit : `${explicit}h`;
    return 'N/A';
  };

  const getGrossHoursForLog = (log) => {
    const explicit = log?.grossHours || log?.workingHours;
    if (explicit && explicit !== 'N/A') return String(explicit).includes('h') ? explicit : `${explicit}h`;
    return 'N/A';
  };

  const getStatusLabel = (status) => {
    if (!status) return 'N/A';
    return String(status)
      .replace(/_/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getAttendanceVisualPercent = (log) => {
    const effectiveMins = parseHoursTextToMinutes(getEffectiveHoursForLog(log));
    const expectedDailyMinutes = 9 * 60;
    return Math.min(100, Math.round((effectiveMins / expectedDailyMinutes) * 100));
  };

  // Handle view details - fetch full log with sessions (same as HRMS)
  const handleViewDetails = async (log) => {
    setSelectedAttendanceRecord(log);
    setShowAttendanceDetails(true);
    setActiveTab('attendance');

    if (!window.history.state?.attendanceDetails) {
      window.history.pushState({ attendanceDetails: true }, '');
    }

    // Fetch full log details with all sessions
    try {
      const logId = log.logId || log.id;
      // Extract actual log ID if it's a transformed ID
      const actualLogId = logId && typeof logId === 'string' && (logId.includes('_current')
        ? logId.split('_current')[0]
        : logId.includes('_session_')
          ? logId.split('_session_')[0]
          : logId) || logId;

      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/${actualLogId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          setSelectedAttendanceRecord(data.data); // Update with full log data including sessions
        }
      }
    } catch (error) {

      // Keep the existing log data if fetch fails
    }
  };

  const validateField = (name, value, currentForm) => {
    switch (name) {
      case 'date':
        if (!value) return 'Date is required';
        return '';
      case 'reason':
        if (!value) return 'Reason is required';
        return '';
      case 'checkIn':
        if (!value) return 'Check In time is required';
        return '';
      case 'checkOut':
        if (!value) return 'Check Out time is required';
        if (currentForm.checkIn && value) {
          const checkInTime = new Date(`2000-01-01T${currentForm.checkIn}`);
          const checkOutTime = new Date(`2000-01-01T${value}`);
          if (checkOutTime <= checkInTime) return 'Check Out time must be after Check In time';
        }
        return '';
      case 'description':
        if (!value || !value.trim()) return 'Description is required';
        if (value.trim().length < 10) return 'Description must be at least 10 characters';
        if (value.trim().length > 500) return 'Description cannot exceed 500 characters';
        return '';
      default:
        return '';
    }
  };

  const handleFieldChange = (name, value) => {
    if (name === 'reason') {
      const reasonObj = dynamicReasons.find(r => (r._id || r.id) === value);
      if (reasonObj) {
        setRegularizationForm(prev => {
          const updatedForm = { ...prev, reason: reasonObj.name, reasonId: value };
          const error = validateField('reason', reasonObj.name, updatedForm);
          setFormErrors(prevErrors => ({ ...prevErrors, reason: error }));
          return updatedForm;
        });
      } else {
        setRegularizationForm(prev => {
          const updatedForm = { ...prev, reason: value, reasonId: "" };
          const error = validateField('reason', value, updatedForm);
          setFormErrors(prevErrors => ({ ...prevErrors, reason: error }));
          return updatedForm;
        });
      }
      return;
    }

    setRegularizationForm(prev => {
      const updatedForm = { ...prev, [name]: value };

      // Update errors immediately based on new value
      const error = validateField(name, value, updatedForm);
      setFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: error,
        // Also re-validate checkout if checkin changes
        ...(name === 'checkIn' ? { checkOut: validateField('checkOut', updatedForm.checkOut, updatedForm) } : {})
      }));

      return updatedForm;
    });
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, regularizationForm[name], regularizationForm);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateRegularizationForm = () => {
    const newErrors = {};
    const allTouched = {};

    ['date', 'reason', 'checkIn', 'checkOut', 'description'].forEach(field => {
      const error = validateField(field, regularizationForm[field], regularizationForm);
      if (error) newErrors[field] = error;
      allTouched[field] = true;
    });

    setFormErrors(newErrors);
    setTouched(allTouched);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleRegularizationSubmit = async () => {
    const validation = validateRegularizationForm();
    if (!validation.isValid) {
      // Get the first error message to show specific validation error
      const errorFields = Object.keys(validation.errors).filter(key => validation.errors[key]);
      if (errorFields.length > 0) {
        const firstError = validation.errors[errorFields[0]];
        alert(firstError || 'Please fix the errors in the form before submitting.');
      } else {
        alert('Please fill all required fields before submitting.');
      }

      // Scroll to first error field for better UX
      setTimeout(() => {
        const firstErrorField = document.querySelector('[aria-invalid="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        alert('Please log in to submit a request');
        setLoading(false);
        return;
      }

      const resolvedReasonId = regularizationForm.reasonId && !regularizationForm.reasonId.startsWith('hardcoded-')
        ? regularizationForm.reasonId
        : null;

      const requestData = {
        date: regularizationForm.date,
        requestedCheckIn: regularizationForm.checkIn,
        requestedCheckOut: regularizationForm.checkOut,
        reason: regularizationForm.reason,
        reasonId: resolvedReasonId,
        description: regularizationForm.description,
        requestType: "both",
        priority: "medium",
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (data.success || data.responseCode === 201) {
        alert('Regularization request submitted successfully!');
        setShowRegularizationModal(false);
        setRegularizationForm({ date: '', checkIn: '', checkOut: '', reason: '', reasonId: '', description: '' });
        setFormErrors({});
        // Reload requests to show the new one
        setRegularizationPage(1);
        await loadRegularizationRequests(1, regularizationPageSize);
      } else {
        // Handle specific validation errors
        if (data.error && data.error.includes("workingHours")) {
          alert("Invalid time range: Check-out time must be after check-in time. Please correct the times.");
        } else if (data.error && data.error.includes("validation failed")) {
          alert("Validation failed: Please check all required fields and ensure times are valid.");
        } else if (data.error && data.error.includes("already exists")) {
          alert("A regularization request already exists for this date.");
        } else {
          alert(`Failed to submit request: ${data.message || data.error || "Unknown error"}`);
        }
      }
    } catch (error) {

      alert('Failed to submit regularization request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderAttendanceCalendar = () => {
    const attendanceTotalItems = Number(pagination?.totalLogs || attendanceLogs.length || 0);
    const attendanceTotalPages = Number(pagination?.totalPages || 1);
    const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
    const recentLogs = attendanceLogs.slice(0, 30);

    const parseHourLabelToMinutes = (hourLabel) => {
      if (!hourLabel) return 0;
      const cleaned = String(hourLabel).replace(/h/gi, '').trim();
      const numeric = Number.parseFloat(cleaned);
      if (Number.isNaN(numeric)) return 0;
      return Math.max(0, Math.round(numeric * 60));
    };

    const totalWorkedMinutes = recentLogs.reduce((acc, log) => {
      const worked = getEffectiveHoursForLog(log);
      return acc + parseHourLabelToMinutes(worked);
    }, 0);

    const averageWorkedMinutes = recentLogs.length > 0 ? Math.round(totalWorkedMinutes / recentLogs.length) : 0;
    const presentLikeDays = recentLogs.filter((log) => ['present', 'on_time', 'late', 'half_day'].includes(String(log.status || '').toLowerCase())).length;
    const punctualityDays = recentLogs.filter((log) => {
      const status = String(log.status || '').toLowerCase();
      return status === 'present' || status === 'on_time';
    }).length;
    const punctualityPercent = presentLikeDays > 0 ? Math.round((punctualityDays / presentLikeDays) * 100) : 0;

    const formatMinutesAsHours = (minutes) => {
      const safe = Math.max(0, Number(minutes) || 0);
      const hrs = Math.floor(safe / 60);
      const mins = safe % 60;
      return `${hrs}h ${mins.toString().padStart(2, '0')}m`;
    };

    const expectedDailyMinutes = 9 * 60;

    const getLiveTodayWorkedMinutes = (todayData) => {
      if (!todayData || !todayData.checkIn) return 0;
      if (todayData.checkOut && todayData.hours > 0) {
        return Math.round(todayData.hours * 60);
      }
      try {
        const parseTimeToMinutes = (timeStr) => {
          if (!timeStr || timeStr === 'N/A') return null;
          const str = String(timeStr).trim();
          const isPM = /pm/i.test(str);
          const isAM = /am/i.test(str);
          const cleaned = str.replace(/[^\d:]/g, '');
          const parts = cleaned.split(':').map(Number);
          if (parts.length >= 2) {
            let h = parts[0];
            const m = parts[1];
            if (isPM && h < 12) h += 12;
            if (isAM && h === 12) h = 0;
            return h * 60 + m;
          }
          return null;
        };

        const inMins = parseTimeToMinutes(todayData.checkIn);
        if (inMins === null) return Math.round((todayData.hours || 0) * 60);

        const now = new Date();
        const currentMins = now.getHours() * 60 + now.getMinutes();

        let outMins = currentMins;
        if (todayData.checkOut) {
          const parsedOut = parseTimeToMinutes(todayData.checkOut);
          if (parsedOut !== null) outMins = parsedOut;
        }

        const diff = outMins - inMins;
        return diff > 0 ? diff : Math.round((todayData.hours || 0) * 60);
      } catch (e) {
        return Math.round((todayData.hours || 0) * 60);
      }
    };

    const todayWorkedMinutes = getLiveTodayWorkedMinutes(attendanceData.today);
    const todayProgress = Math.min(100, Math.round((todayWorkedMinutes / expectedDailyMinutes) * 100));
    const hasActiveWfhSession = wfhToday.approvedToday && Boolean(attendanceData.today.checkIn) && !attendanceData.today.checkOut;

    const handleAttendancePageChange = (page) => {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const shouldShowPagination = !loading && attendanceTotalPages > 1 && attendanceTotalItems > effectivePaginationLimit;

    return (
      <div className="space-y-6">
        {/* Attendance Overview cards (SaaS Premium Cards Grid) */}
        <div className="bg-white border border-slate-200/90 rounded-2xl shadow-2xs p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500/10 rounded-xl border border-blue-500/20">
                <Timer className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900">Attendance Overview</h2>
                <p className="text-xs text-slate-500">
                  {formatDate(new Date(), { includeTime: false })} · Live employee attendance metrics
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
            {/* ATTENDANCE STATS CARD */}
            <div className="xl:col-span-4 p-5 rounded-2xl border border-blue-200/80 bg-slate-50/60 shadow-2xs hover:shadow-md hover:border-blue-400 transition-all duration-200 flex flex-col justify-between group">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider group-hover:text-blue-600 transition-colors">Attendance Stats</span>
                <input
                  type="month"
                  value={selectedStatsMonth}
                  max={new Date().toISOString().slice(0, 7)}
                  onChange={(e) => setSelectedStatsMonth(e.target.value)}
                  onClick={(e) => e.target.showPicker()}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 cursor-pointer shadow-2xs"
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-slate-200/80 shadow-2xs hover:border-blue-300 transition-all">
                  <span className="text-xs font-semibold text-slate-500">Today</span>
                  <span className="text-sm font-bold text-slate-900">{formatMinutesAsHours(todayWorkedMinutes)}</span>
                </div>
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-slate-200/80 shadow-2xs hover:border-blue-300 transition-all">
                  <span className="text-xs font-semibold text-slate-500">Monthly Avg</span>
                  <span className="text-sm font-bold text-slate-900">
                    {monthlyStats.loading ? "..." : formatMinutesAsHours(monthlyStats.averageWorkedMinutes)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-white border border-slate-200/80 shadow-2xs hover:border-blue-300 transition-all">
                  <span className="text-xs font-semibold text-slate-500">Punctuality</span>
                  <span className="text-sm font-bold text-emerald-600">
                    {monthlyStats.loading ? "..." : `${monthlyStats.punctualityPercent}%`}
                  </span>
                </div>
              </div>
            </div>

            {/* TIMINGS CARD */}
            <div className="xl:col-span-4 p-5 rounded-2xl border border-cyan-200/80 bg-slate-50/60 shadow-2xs hover:shadow-md hover:border-cyan-400 transition-all duration-200 flex flex-col justify-between group">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-4 block group-hover:text-cyan-600 transition-colors">Timings & Hours</span>
              <div className="bg-white border border-slate-200/80 rounded-xl p-4 mb-4 shadow-2xs">
                <div className="flex items-center justify-between text-xs font-bold text-slate-600">
                  <span>In: {formatTimeAMPM(attendanceData.today.checkIn) || 'N/A'}</span>
                  <span>Out: {formatTimeAMPM(attendanceData.today.checkOut) || 'N/A'}</span>
                </div>
                <div className="mt-3 h-2 bg-blue-100/80 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full transition-all duration-500"
                    style={{ width: `${todayProgress}%` }}
                  />
                </div>
                <div className="mt-2.5 flex items-center justify-between text-[11px] font-medium text-slate-500">
                  <span>Today {formatMinutesAsHours(todayWorkedMinutes)}</span>
                  <span className="font-bold text-blue-600">{todayProgress}% of standard day</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-white border border-slate-200/80 shadow-2xs hover:border-cyan-300 transition-all">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Clock In</p>
                  <p className="text-sm font-bold text-slate-800 mt-1">{formatTimeAMPM(attendanceData.today.checkIn) || 'N/A'}</p>
                </div>
                <div className="p-3.5 rounded-xl bg-white border border-slate-200/80 shadow-2xs hover:border-cyan-300 transition-all">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Clock Out</p>
                  <p className="text-sm font-bold text-slate-800 mt-1">{formatTimeAMPM(attendanceData.today.checkOut) || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* ACTIONS CARD */}
            <div className="xl:col-span-4 p-5 rounded-2xl border border-indigo-200/80 bg-slate-50/60 shadow-2xs hover:shadow-md hover:border-indigo-400 transition-all duration-200 flex flex-col justify-between group">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3 block group-hover:text-indigo-600 transition-colors">Actions</span>
              <div className="bg-white border border-slate-200/80 rounded-xl p-3.5 mb-3 shadow-2xs flex items-center justify-between">
                <div>
                  <p className="text-xl font-extrabold text-slate-800 leading-none">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                  <p className="text-xs font-semibold text-slate-400 mt-1">{formatDate(new Date(), { includeTime: false })}</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100 group-hover:scale-105 transition-transform">
                  <Clock className="w-4 h-4" />
                </div>
              </div>
              <div className="space-y-2">
                {wfhToday.approvedToday && !(attendanceData.today.checkIn && attendanceData.today.checkOut) && (
                  <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50/60">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">WFH Approved Today</p>
                        <p className="text-xs text-emerald-700 mt-0.5 font-medium">Remote attendance enabled</p>
                      </div>
                      <Home className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    </div>
                    <button
                      type="button"
                      onClick={hasActiveWfhSession ? handleWfhClockOut : handleWfhClockIn}
                      disabled={wfhPunchLoading}
                      className={`mt-2.5 w-full px-3 py-2 rounded-xl text-xs font-bold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${hasActiveWfhSession
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                    >
                      {wfhPunchLoading
                        ? 'Saving...'
                        : hasActiveWfhSession ? 'Check Out' : 'Check In'}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setActiveTab('regularization')}
                  className="w-full text-left px-3.5 py-2.5 rounded-xl border border-slate-200/80 bg-white text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-all shadow-2xs cursor-pointer"
                >
                  Request Attendance Correction
                </button>
                <button
                  onClick={() => window.location.assign('/my_wfh_requests')}
                  className="w-full text-left px-3.5 py-2.5 rounded-xl border border-slate-200/80 bg-white text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-all shadow-2xs cursor-pointer"
                >
                  Submit WFH Request
                </button>
                <button
                  onClick={() => window.location.assign('/policies')}
                  className="w-full text-left px-3.5 py-2.5 rounded-xl border border-slate-200/80 bg-white text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 transition-all shadow-2xs cursor-pointer"
                >
                  Attendance Policy
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* UI refactor: Logs table + Filters readability */}
        <div className="bg-white border border-slate-200/90 rounded-2xl shadow-2xs p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-[#DBEAFE] rounded-full flex-shrink-0">
                <FileText className="w-5 h-5 text-[#0F2942]" />
              </div>
              <div>
                <h2 className="text-[18px] font-semibold text-[#111827]">Logs & Requests</h2>
                <p className="text-[12px] text-[#6B7280] mt-2">Attendance Log · Calendar · Attendance Requests</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant={selectedRows.size > 0 ? "solid" : "outline"}
                  icon={
                    exportLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )
                  }
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={`rounded-lg font-semibold text-[14px] px-4 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-[#DBEAFE] ${selectedRows.size > 0
                    ? "bg-[#0F2942] hover:bg-[#0F2942]/90 text-white border-transparent"
                    : "text-[#0F2942] border-[#E5E7EB] bg-white hover:bg-[#DBEAFE]"
                    }`}
                  aria-label={
                    selectedRows.size > 0
                      ? `Export ${selectedRows.size} selected attendance records`
                      : 'Export attendance logs'
                  }
                >
                  {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export Logs'}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleMyAttendanceSelectedFormatExport}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4 bg-white rounded-2xl border border-[#E5E7EB] mb-4">
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">Status Filter</label>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px] text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#DBEAFE] transition-all cursor-pointer"
              >
                <option value="all">All Statuses</option>
                <option value="present">Present</option>
                <option value="late">Late Arrival</option>
                <option value="half_day">Half Day</option>
                <option value="absent">Absent</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">Start Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
                onClick={(e) => {
                  try {
                    e.target.showPicker?.();
                  } catch {
                    // showPicker requires a user gesture; ignore if unavailable
                  }
                }}
                className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px] text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#DBEAFE] transition-all cursor-pointer"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[12px] font-semibold text-[#6B7280] uppercase tracking-wide">End Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
                onClick={(e) => {
                  try {
                    e.target.showPicker?.();
                  } catch {
                    // showPicker requires a user gesture; ignore if unavailable
                  }
                }}
                className="w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[14px] text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#DBEAFE] transition-all cursor-pointer"
              />
            </div>
            <div className="flex items-end pb-2">
              <button
                onClick={clearFilters}
                className="text-[12px] font-semibold text-[#6B7280] hover:text-[#0F2942] transition-colors px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#DBEAFE]"
              >
                Clear all filters
              </button>
            </div>
          </div>

          {loading && !initialLoading ? (
            <div className="py-20 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942] mx-auto"></div>
              <p className="mt-4 font-bold text-[#6B7280] uppercase tracking-widest text-xs">Fetching logs...</p>
            </div>
          ) : attendanceLogs.length === 0 ? (
            <div className="py-12 text-center bg-[#F9FAFB] rounded-2xl border border-dashed border-[#E5E7EB]">
              <div className="mb-4 inline-flex p-6 bg-white rounded-full shadow-sm text-[#6B7280]">
                <FileText className="w-12 h-12" />
              </div>
              <p className="font-semibold text-[#111827] text-[14px]">No logs found</p>
              <p className="text-[#6B7280] text-[12px] max-w-xs mx-auto mt-2">Adjust your filters or try a different date range.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border border-[#E5E7EB] overflow-hidden">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-[#DBEAFE] text-[#6B7280]">
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Attendance Visual</th>
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Punch In</th>
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Punch Out</th>
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Effective Hours</th>
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Gross Hours</th>
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Arrival</th>
                      <th className="px-4 py-3 text-left font-semibold text-[12px] uppercase tracking-wide">Log</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {attendanceLogs.map((log, idx) => {
                      const logId = log.id || log.logId;
                      const freeLateNum = log.checkinLate
                        ? (freeLateIndexMap.get(String(log.id || '')) ||
                           freeLateIndexMap.get(String(log.logId || '')) ||
                           freeLateIndexMap.get(String(log.date || '')) ||
                           null)
                        : null;
                      const isFreeLate = Boolean(freeLateNum);
                      const effectiveHours = getEffectiveHoursForLog(log);
                      const grossHours = getGrossHoursForLog(log);
                      const statusLabel = freeLateNum
                        ? getOrdinalGraceLabel(freeLateNum)
                        : getStatusLabel(log.status);
                      const effectiveStatus = (log.status === 'late' && isFreeLate) ? 'present' : log.status;
                      const visualPct = getAttendanceVisualPercent(log);
                      return (
                        <tr
                          key={logId}
                          className={`transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'
                            } hover:bg-[#DBEAFE]`}
                        >
                          <td className="px-4 py-3">
                            <div className="font-semibold text-[#111827]">{new Date(log.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                            <div className="text-[12px] text-[#6B7280] mt-2">{new Date(log.date).toLocaleDateString('en-GB', { weekday: 'long' })}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="w-40">
                              <div className="h-2 bg-[#DBEAFE] rounded-full overflow-hidden">
                                <div className="h-full bg-[#0F2942] rounded-full" style={{ width: `${visualPct}%` }} />
                              </div>
                              <p className="text-[12px] text-[#6B7280] mt-2">{visualPct}%</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[#111827]">
                            <div className="flex flex-col">
                              <span>{formatTimeAMPM(log.checkIn) || 'N/A'}</span>
                              {log.checkinLate && log.checkinLateTime && !isFreeLate && (
                                <span className="text-[10px] text-red-500 font-bold">Late ({log.checkinLateTime})</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[#111827]">
                            <div className="flex flex-col">
                              <span>{formatTimeAMPM(log.checkOut) || 'N/A'}</span>
                              {log.checkoutEarly && log.checkoutEarlyTime && (
                                <span className="text-[10px] text-red-500 font-bold">Early Left ({log.checkoutEarlyTime})</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[#111827]">{effectiveHours}</td>
                          <td className="px-4 py-3 font-semibold text-[#111827]">{grossHours}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-[12px] font-semibold ${getStatusColor(effectiveStatus)}`}>
                              {statusLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewDetails(log)}
                                title="View details"
                                className="p-1 rounded hover:bg-[#DBEAFE] transition-colors"
                              >
                                <Eye className="w-4 h-4 text-[#0F2942]" />
                              </button>
                              <button
                                onClick={() => {
                                  setRegModalPrefilledData({
                                    date: log.date,
                                    checkIn: log.checkIn || '',
                                    checkOut: log.checkOut || '',
                                  });
                                  setShowNewRegModal(true);
                                }}
                                title="Raise regularization request"
                                className="p-1 rounded hover:bg-[#DBEAFE] transition-colors"
                              >
                                <CalendarIcon className="w-4 h-4 text-orange-500 hover:text-orange-800" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {shouldShowPagination && (
                  renderPagination(
                    currentPage,
                    attendanceTotalPages,
                    attendanceTotalItems,
                    effectivePaginationLimit,
                    handleAttendancePageChange,
                    handleItemsPerPageChange
                  )
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDayView = () => (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] !mt-0 p-4">
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-zoomIn flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="bg-slate-900 p-8 text-white relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
          <div className="flex items-center justify-between relative z-10">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">Timeline Summary</h3>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">
                {selectedDate ? new Date(selectedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Today'}
              </p>
            </div>
            <button
              onClick={() => setShowDayView(false)}
              className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-colors group"
            >
              <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          <div className="grid grid-cols-2 gap-4 mb-10">
            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 group hover:bg-white hover:shadow-lg transition-all">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Check In</p>
              <p className="text-2xl font-bold text-slate-800">{formatTimeAMPM(attendanceData.today.checkIn)}</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 group hover:bg-white hover:shadow-lg transition-all">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Check Out</p>
              <p className="text-2xl font-bold text-slate-800">{formatTimeAMPM(attendanceData.today.checkOut) || '--:--'}</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-2xl bg-slate-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-slate-600" />
              </div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Punch Timeline</h4>
            </div>

            <div className="space-y-0 relative pl-4">
              <div className="absolute left-[7.5px] top-2 bottom-2 w-0.5 bg-slate-100"></div>
              {attendanceData.today.punches.map((punch, index) => (
                <div key={index} className="flex gap-6 pb-8 last:pb-0 relative">
                  <div className="w-4 h-4 rounded-full bg-white border-4 border-slate-900 z-10 -ml-[4.25px] mt-2 shadow-sm"></div>
                  <div className="flex-1 p-5 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-lg font-bold text-slate-800">{formatTimeAMPM(punch.time)}</span>
                      <span className="px-3 py-0.5 bg-slate-100 rounded-full text-[9px] font-bold uppercase tracking-widest text-slate-500">
                        {punch.type || 'Punch'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400 text-xs font-bold">
                      <MapPin className="w-3 h-3" />
                      <span>{punch.location || 'Office Terminal'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-slate-50 bg-slate-50/30 flex justify-center">
          <button
            onClick={() => setShowDayView(false)}
            className="px-10 py-3 bg-slate-900 text-white rounded-2xl font-bold uppercase tracking-widest text-xs shadow-xl shadow-slate-200 hover:scale-105 transition-all"
          >
            Close View
          </button>
        </div>
      </div>
    </div>
  );

  // Pagination helper function
  const renderPagination = (
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
    onLimitChange,
    wrapInCard = false
  ) => {
    if (totalPages <= 1) return null;
    const paginationEl = (
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        limit={itemsPerPage}
        hasNextPage={currentPage < totalPages}
        hasPrevPage={currentPage > 1}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        border={false}
      />
    );
    return wrapInCard ? (
      <div className="mt-6 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
        {paginationEl}
      </div>
    ) : paginationEl;
  };

  const renderRegularization = () => {
    const totalRegularizationRequests = regularizationPagination.totalItems || regularizationRequests.length;
    const totalRegularizationPages =
      regularizationPagination.totalPages ||
      Math.max(1, Math.ceil((regularizationPagination.totalItems || regularizationRequests.length) / regularizationPageSize));
    const regularizationStartIndex = (regularizationPage - 1) * regularizationPageSize;
    const regularizationEndIndex = regularizationStartIndex + regularizationRequests.length;

    const handleRegularizationPageChange = (page) => {
      setRegularizationPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-6">
        {/* UI refactor: Regularization cards + table readability */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#DBEAFE] rounded-lg">
                <Edit className="w-5 h-5 text-[#0F2942]" />
              </div>
              <h2 className="text-[18px] font-semibold text-[#111827]">Regularization Requests</h2>
            </div>

            <button
              onClick={() => setShowNewRegModal(true)}
              className="group flex items-center gap-2 px-4 py-2 bg-[#0F2942] text-white rounded-lg font-semibold text-[14px] transition-all hover:bg-[#0F2942]/90 focus:outline-none focus:ring-2 focus:ring-[#0F2942]/20"
            >
              <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform" />
              <span>New Request</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            {regularizationRequests.length === 0 ? (
              <div className="py-10 text-center bg-[#F9FAFB] rounded-2xl border border-dashed border-[#E5E7EB]">
                <div className="mb-3 inline-flex p-4 bg-white rounded-full shadow-sm text-[#6B7280]">
                  <FileText className="w-12 h-12" />
                </div>
                <p className="font-semibold text-[#111827] text-[14px]">No requests yet</p>
                <p className="text-[#6B7280] text-[12px] max-w-xs mx-auto mt-2">Submit a regularization request to see it here.</p>
              </div>
            ) : (
              <table className="w-full text-[14px] border border-[#E5E7EB] rounded-2xl overflow-hidden">
                <thead className="bg-[#DBEAFE] text-[#6B7280]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Regularization Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Check In</th>
                    <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Check Out</th>
                    <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Reason</th>
                    <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Applied Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  {regularizationRequests.map((request, idx) => (
                    <tr
                      key={request.id}
                      className={`transition-colors border-t border-[#E5E7EB] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'
                        } hover:bg-[#DBEAFE]`}
                    >
                      <td className="px-4 py-3 text-[#111827] font-semibold">{formatDate(request.date, { includeTime: false })}</td>
                      <td className="px-4 py-3 text-[#6B7280]">{formatTimeAMPM(request.checkIn) || 'N/A'}</td>
                      <td className="px-4 py-3 text-[#6B7280]">{formatTimeAMPM(request.checkOut) || 'N/A'}</td>
                      <td className="px-4 py-3 text-[#6B7280] capitalize">{request.reason}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-[12px] font-semibold capitalize ${getStatusColor(request.status)}`}>
                          {request.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#6B7280]">{formatDate(request.submittedAt, { includeTime: true })}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedRegRequest(request)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-[12px] font-semibold text-[#0F2942] bg-[#DBEAFE] hover:bg-[#BFDBFE] rounded-lg transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {renderPagination(
              regularizationPage,
              totalRegularizationPages,
              totalRegularizationRequests,
              regularizationPageSize,
              handleRegularizationPageChange,
              (limit) => {
                setRegularizationPageSize(limit);
                setRegularizationPage(1);
              }
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCompOffOvertime = () => {
    // Calculate pagination for comp-off history
    const totalCompOffHistory = compOffData.history.length;
    const totalCompOffPages = Math.ceil(totalCompOffHistory / compOffPageSize);
    const compOffStartIndex = (compOffPage - 1) * compOffPageSize;
    const paginatedCompOffHistory = compOffData.history.slice(compOffStartIndex, compOffStartIndex + compOffPageSize);
    const compOffEndIndex = compOffStartIndex + paginatedCompOffHistory.length;

    const handleCompOffPageChange = (page) => {
      setCompOffPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Calculate pagination for overtime history
    const totalOvertimeHistory = overtimeData.history.length;
    const totalOvertimePages = Math.ceil(totalOvertimeHistory / overtimePageSize);
    const overtimeStartIndex = (overtimePage - 1) * overtimePageSize;
    const paginatedOvertimeHistory = overtimeData.history.slice(overtimeStartIndex, overtimeStartIndex + overtimePageSize);
    const overtimeEndIndex = overtimeStartIndex + paginatedOvertimeHistory.length;

    const handleOvertimePageChange = (page) => {
      setOvertimePage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-12 animate-fadeIn">
        {/* Comp-Off Section */}
        <div className="glass-card bg-white p-8 shadow-xl border border-slate-100">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 mb-10">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-rose-600 rounded-2xl">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Compensatory Off</h2>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Manage your extra day credits</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 sm:gap-8">
              {[
                { label: 'Available', val: compOffData.available, color: 'emerald' },
                { label: 'Used', val: compOffData.used, color: 'slate' },
                { label: 'Pending', val: compOffData.pending, color: 'amber' }
              ].map((stat, i) => (
                <div key={i} className="text-center group">
                  <p className={`text-3xl font-bold transition-colors text-${stat.color === 'slate' ? 'slate-400' : stat.color + '-600'}`}>
                    {stat.val}
                  </p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-50 pb-2 ml-1">Historical Records</h4>
            {paginatedCompOffHistory.length === 0 ? (
              <div className="py-12 text-center bg-slate-50/50 rounded-3xl border border-dashed border-slate-200">
                <p className="font-bold text-slate-400 uppercase tracking-widest text-xs">No comp-off history found</p>
              </div>
            ) : (
              paginatedCompOffHistory.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-5 bg-slate-50/50 rounded-2xl border border-white hover:bg-white hover:shadow-md transition-all group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center font-bold text-slate-400 group-hover:text-rose-600 transition-colors">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-bold text-slate-800 text-lg">{formatDate(item.date, { includeTime: false })}</p>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-0.5">{item.reason}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-800 text-xl">{item.hours}h</p>
                    <span className={`inline-flex px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest mt-1 ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          {renderPagination(
            compOffPage,
            totalCompOffPages,
            totalCompOffHistory,
            compOffPageSize,
            handleCompOffPageChange,
            (limit) => {
              setCompOffPageSize(limit);
              setCompOffPage(1);
            },
            true // wrapInCard
          )}
        </div>

        {/* Overtime Section */}
        <div className="glass-card bg-slate-900 p-8 shadow-2xl border border-slate-800 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/10 rounded-full blur-3xl -mr-32 -mt-32"></div>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 mb-10 relative z-10">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-violet-600 rounded-2xl">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white tracking-tight">Overtime (OT)</h2>
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-1">Extra work hour accumulation</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 sm:gap-8">
              {[
                { label: 'Accrued', val: `${overtimeData.total}h`, color: 'blue' },
                { label: 'Settled', val: `${overtimeData.used}h`, color: 'emerald' },
                { label: 'Pending', val: `${overtimeData.pending}h`, color: 'amber' }
              ].map((stat, i) => (
                <div key={i} className="text-center group">
                  <p className={`text-3xl font-bold transition-colors text-${stat.color}-400`}>
                    {stat.val}
                  </p>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 relative z-10">
            <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-4 border-b border-slate-800 pb-2 ml-1">Activity Log</h4>
            {paginatedOvertimeHistory.length === 0 ? (
              <div className="py-12 text-center bg-white/5 rounded-3xl border border-dashed border-white/10">
                <p className="font-bold text-slate-600 uppercase tracking-widest text-xs">No overtime entries recorded</p>
              </div>
            ) : (
              paginatedOvertimeHistory.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/5 hover:bg-white/10 transition-all group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center font-bold text-slate-600 group-hover:text-violet-400 transition-colors">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-bold text-white text-lg">{formatDate(item.date, { includeTime: false })}</p>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-0.5">{item.reason}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-white text-xl">{item.hours}h</p>
                    <span className={`inline-flex px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest mt-1 ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          {renderPagination(
            overtimePage,
            totalOvertimePages,
            totalOvertimeHistory,
            overtimePageSize,
            handleOvertimePageChange,
            (limit) => {
              setOvertimePageSize(limit);
              setOvertimePage(1);
            },
            true // wrapInCard
          )}
        </div>
      </div>
    );
  };

  const renderHolidayCalendar = () => (
    <div className="animate-fadeIn">
      <HolidayCalendar
        onBack={() => setActiveTab('attendance')}
        userRole="employee"
      />
    </div>
  );

  const renderShiftRoster = () => {
    const totalRosterItems = rosterPagination.totalItems || shiftData.roster.length;
    const totalRosterPages =
      rosterPagination.totalPages ||
      Math.max(1, Math.ceil((rosterPagination.totalItems || shiftData.roster.length) / rosterPageSize));
    const rosterStartIndex = (rosterPage - 1) * rosterPageSize;
    const rosterEndIndex = rosterStartIndex + shiftData.roster.length;

    const handleRosterPageChange = (page) => {
      setRosterPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-md">
                <ClockIcon className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-[16px] font-semibold text-slate-700">Current Shift</h2>
            </div>
            {shiftData.current.isRosterOverride && (
              <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[11px] font-medium">
                Roster Override
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
              <h4 className="text-[13px] font-semibold text-slate-700 mb-4">Shift Configuration</h4>
              <div className="space-y-4">
                {[
                  { label: 'Shift Name', val: shiftData.current.name || 'Default' },
                  { label: 'Time window', val: `${formatTimeAMPM(shiftData.current.startTime)} - ${formatTimeAMPM(shiftData.current.endTime)}` },
                  { label: 'Break Period', val: `${shiftData.current.breakDuration || 0} Minutes` }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-[12px] font-medium text-slate-500">{item.label}</span>
                    <span className="text-[13px] font-medium text-slate-700">{item.val}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
              <h4 className="text-[13px] font-semibold text-slate-700 mb-4">Active Working Days</h4>
              {shiftData.current.workingDays && shiftData.current.workingDays.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {shiftData.current.workingDays.map((day, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-md border border-slate-200">
                      <CheckCircle className="w-4 h-4 text-indigo-600" />
                      <span className="text-[12px] font-medium text-slate-700 capitalize">{day}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-slate-500">No working days specified</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#DBEAFE] rounded-lg">
              <CalendarIcon className="w-5 h-5 text-[#0F2942]" />
            </div>
            <h2 className="text-[18px] font-semibold text-[#111827]">Assigned Roster</h2>
          </div>

          <div>
            {totalRosterItems === 0 ? (
              <div className="py-10 text-center bg-[#F9FAFB] rounded-2xl border border-dashed border-[#E5E7EB]">
                <p className="text-[13px] font-medium text-[#6B7280]">No roster assignments found</p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-[#E5E7EB] rounded-2xl">
                <table className="w-full text-[14px]">
                  <thead className="bg-[#DBEAFE] text-[#6B7280]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Date Range</th>
                      <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Shift</th>
                      <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Time Window</th>
                      <th className="text-left px-4 py-3 font-semibold text-[12px] uppercase tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {shiftData.roster.map((roster, index) => (
                      <tr
                        key={index}
                        className={`border-t border-[#E5E7EB] hover:bg-[#DBEAFE] ${index % 2 === 0 ? 'bg-white' : 'bg-[#F9FAFB]'
                          }`}
                      >
                        <td className="px-4 py-3 text-[#111827] font-semibold">
                          {roster.date === roster.endDate
                            ? formatDate(roster.date, { includeTime: false })
                            : `${formatDate(roster.date, { includeTime: false })} - ${formatDate(roster.endDate, { includeTime: false })}`
                          }
                        </td>
                        <td className="px-4 py-3 text-[#6B7280]">{roster.shift}</td>
                        <td className="px-4 py-3 text-[#6B7280]">
                          {roster.startTime !== 'N/A' && roster.endTime !== 'N/A'
                            ? `${formatTimeAMPM(roster.startTime)} - ${formatTimeAMPM(roster.endTime)}`
                            : 'N/A'
                          }
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 rounded-full text-[12px] font-semibold ${roster.status === 'Active'
                              ? 'bg-[#16A34A]/10 text-[#16A34A] border border-[#E5E7EB]'
                              : 'bg-[#F9FAFB] text-[#6B7280] border border-[#E5E7EB]'
                              }`}
                          >
                            {roster.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {renderPagination(
              rosterPage,
              totalRosterPages,
              totalRosterItems,
              rosterPageSize,
              handleRosterPageChange,
              (limit) => {
                setRosterPageSize(limit);
                setRosterPage(1);
              }
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRegularizationModal = () => (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] !mt-0 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-zoomIn flex flex-col max-h-[90vh] font-sans">
        {/* Modal Header */}
        <div className="bg-indigo-700 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[28px] font-semibold leading-tight">New Correction Request</h3>
              <p className="text-indigo-100 text-[12px] font-medium mt-1">Regularize your attendance logs</p>
            </div>
            <button
              onClick={() => {
                setShowRegularizationModal(false);
                setRegularizationForm({ date: '', checkIn: '', checkOut: '', reason: '', reasonId: '', description: '' });
                setFormErrors({});
                setTouched({});
              }}
              className="p-2.5 bg-white/15 hover:bg-white/25 rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={(e) => { e.preventDefault(); handleRegularizationSubmit(); }} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date Input */}
              <div className="space-y-2">
                <label className="text-[12px] font-medium text-slate-500 ml-1">
                  Correction Date <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={regularizationForm.date}
                    onChange={(e) => handleFieldChange('date', e.target.value)}
                    onBlur={() => handleBlur('date')}
                    onClick={(e) => e.target.showPicker()}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full h-12 px-4 rounded-md border bg-slate-50 text-[13px] font-medium transition-all outline-none focus:bg-white ${touched.date && formErrors.date
                      ? 'border-rose-300 text-rose-600'
                      : 'border-slate-200 text-slate-700 focus:border-indigo-500'
                      }`}
                  />
                  {touched.date && formErrors.date && (
                    <p className="text-[11px] font-medium text-rose-500 mt-1.5 ml-1">{formErrors.date}</p>
                  )}
                </div>
              </div>

              {/* Reason Dropdown */}
              <div className="space-y-2">
                <label className="text-[12px] font-medium text-slate-500 ml-1">
                  Revision Reason <span className="text-rose-500">*</span>
                </label>
                <select
                  value={regularizationForm.reasonId || regularizationForm.reason}
                  onChange={(e) => handleFieldChange('reason', e.target.value)}
                  onBlur={() => handleBlur('reason')}
                  required
                  className={`w-full h-12 px-4 rounded-md border bg-slate-50 text-[13px] font-medium transition-all outline-none focus:bg-white cursor-pointer ${touched.reason && formErrors.reason
                    ? 'border-rose-300 text-rose-600'
                    : 'border-slate-200 text-slate-700 focus:border-indigo-500'
                    }`}
                >
                  <option value="">Select a reason</option>
                  {dynamicReasons.map((reason) => (
                    <option key={reason._id || reason.id} value={reason._id || reason.id}>
                      {reason.name}
                    </option>
                  ))}
                </select>
                {touched.reason && formErrors.reason && (
                  <p className="text-[11px] font-medium text-rose-500 mt-1.5 ml-1">{formErrors.reason}</p>
                )}
              </div>

              {/* Times */}
              <div className="space-y-2">
                <label className="text-[12px] font-medium text-slate-500 ml-1">
                  Adjusted In <span className="text-rose-500">*</span>
                </label>
                <input
                  type="time"
                  value={regularizationForm.checkIn}
                  onChange={(e) => handleFieldChange('checkIn', e.target.value)}
                  onBlur={() => handleBlur('checkIn')}
                  onClick={(e) => e.target.showPicker()}
                  required
                  className="w-full h-12 px-4 rounded-md border border-slate-200 bg-slate-50 text-[13px] font-medium text-slate-700 transition-all outline-none focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[12px] font-medium text-slate-500 ml-1">
                  Adjusted Out <span className="text-rose-500">*</span>
                </label>
                <input
                  type="time"
                  value={regularizationForm.checkOut}
                  onChange={(e) => handleFieldChange('checkOut', e.target.value)}
                  onBlur={() => handleBlur('checkOut')}
                  onClick={(e) => e.target.showPicker()}
                  required
                  className="w-full h-12 px-4 rounded-md border border-slate-200 bg-slate-50 text-[13px] font-medium text-slate-700 transition-all outline-none focus:bg-white focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2 relative">
              <label className="text-[12px] font-medium text-slate-500 ml-1">
                Detailed Rationale <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={regularizationForm.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                onBlur={() => handleBlur('description')}
                required
                rows={4}
                maxLength={1500}
                placeholder="Explain the reason for this correction (e.g. system downtime, forgot to punch)..."
                className={`w-full p-4 rounded-md border bg-slate-50 text-[13px] font-medium text-slate-700 transition-all outline-none focus:bg-white ${touched.description && formErrors.description
                  ? 'border-rose-300'
                  : 'border-slate-200 focus:border-indigo-500'
                  }`}
              />
              <div className="flex items-center justify-between mt-2 px-2">
                {touched.description && formErrors.description ? (
                  <p className="text-[11px] font-medium text-rose-500">{formErrors.description}</p>
                ) : <span></span>}
                <span className={`text-[11px] font-medium ${regularizationForm.description.length >= 1500 ? 'text-rose-500' : 'text-slate-400'}`}>
                  {regularizationForm.description.length} / 1500
                </span>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-5 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setShowRegularizationModal(false);
                  setRegularizationForm({ date: '', checkIn: '', checkOut: '', reason: '', reasonId: '', description: '' });
                  setFormErrors({});
                  setTouched({});
                }}
                className="w-full sm:w-auto px-8 py-3 text-slate-500 text-[13px] font-semibold hover:text-slate-700 transition-colors"
              >
                Discard
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:flex-1 py-3 bg-indigo-600 text-white rounded-md text-[13px] font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Post Request</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'regularization', label: 'Regularization', icon: Edit },
    { id: 'holidays', label: 'Holidays', icon: CalendarIcon },
    { id: 'shift', label: 'Shift/Roster', icon: ClockIcon }
  ];

  const renderAttendanceDetails = () => {
    if (!showAttendanceDetails || !selectedAttendanceRecord) {
      return null;
    }

    const record = selectedAttendanceRecord;
    const recordFreeLateNum = record.checkinLate
      ? (freeLateIndexMap.get(String(record.id || '')) ||
         freeLateIndexMap.get(String(record.logId || '')) ||
         freeLateIndexMap.get(String(record.date || '')) ||
         null)
      : null;
    const isRecordFreeLate = Boolean(recordFreeLateNum);
    const formattedDate = new Date(record.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const effectiveHours = getEffectiveHoursForLog(record);
    const grossHours = getGrossHoursForLog(record);
    const statusLabel = recordFreeLateNum === 1
      ? '1st Late'
      : recordFreeLateNum === 2
        ? '2nd Late'
        : getStatusLabel(record.status);
    const visualPct = getAttendanceVisualPercent(record);
    const shiftStartTime = record?.shiftDetails?.siftStartTime || 'N/A';
    const shiftEndTime = record?.shiftDetails?.siftEndTime || 'N/A';
    const shiftWindowLabel = (shiftStartTime !== 'N/A' && shiftEndTime !== 'N/A')
      ? `${formatTimeAMPM(shiftStartTime)} - ${formatTimeAMPM(shiftEndTime)}`
      : 'N/A';
    const normalizedStatus = String(record.status || '').toLowerCase();

    // Derive sessions from raw punches if they don't exist
    const derivedCheckInPunches = record.checkInPunches || (record.punches ? record.punches.filter((_, idx) => idx % 2 === 0).map(p => p.time) : []);
    const derivedCheckOutPunches = record.checkOutPunches || (record.punches ? record.punches.filter((_, idx) => idx % 2 !== 0).map(p => p.time) : []);

    // Attach derived punches to record for easier use in sub-components
    const enhancedRecord = {
      ...record,
      checkInPunches: derivedCheckInPunches,
      checkOutPunches: derivedCheckOutPunches
    };

    return (
      <div className="min-h-screen bg-[#F9FAFB] p-4 sm:p-6 animate-fadeIn font-sans">
        <div className="max-w-7xl mx-auto">
          {/* Top Navigation */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => {
                if (window.history.state?.attendanceDetails) {
                  window.history.back();
                  return;
                }

                closeAttendanceDetails();
              }}
              className="w-10 h-10 bg-white rounded-2xl border border-[#E5E7EB] text-[#6B7280] hover:text-[#111827] transition-colors flex items-center justify-center shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-[#111827]">Record Analysis</h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="px-2 py-1 bg-[#EFF6FF] text-[#0F2942] rounded-lg text-xs font-medium uppercase">Live Data</span>
                <p className="text-xs text-[#6B7280] font-medium">Reference: {record.id || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="mb-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-sm p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-[#111827] mb-4">Attendance Record View</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <div className="p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                <p className="text-xs text-[#6B7280]">Date</p>
                <p className="mt-2 text-sm font-medium text-[#111827]">{formattedDate}</p>
              </div>
              <div className="p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                <p className="text-xs text-[#6B7280]">Attendance Visual</p>
                <div className="mt-2 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div className="h-full bg-[#0F2942] rounded-full" style={{ width: `${visualPct}%` }} />
                </div>
                <p className="mt-2 text-sm font-medium text-[#111827]">{visualPct}%</p>
              </div>
              <div className="p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                <p className="text-xs text-[#6B7280]">Effective Hours</p>
                <p className="mt-2 text-sm font-semibold text-[#111827]">{effectiveHours}</p>
              </div>
              <div className="p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                <p className="text-xs text-[#6B7280]">Gross Hours</p>
                <p className="mt-2 text-sm font-semibold text-[#111827]">{grossHours}</p>
              </div>
              <div className="p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                <p className="text-xs text-[#6B7280]">Status</p>
                <p className="mt-2 text-sm font-semibold text-[#111827]">{statusLabel}</p>
              </div>
              <div className="p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                <p className="text-xs text-[#6B7280]">Log</p>
                <p className="mt-2 text-sm font-semibold text-[#111827]">Punch Record Available</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 items-start">
            {/* Summary Panel - 30% */}
            <div className="lg:col-span-3 space-y-4">
              <div className="bg-white p-4 border border-[#E5E7EB] rounded-2xl shadow-sm">
                <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-4 border-b border-[#E5E7EB] pb-3">Digital Identity</h3>

                <div className="space-y-6 relative z-10">
                  {record.employeeName && (
                    <div className="space-y-1">
                      <p className="text-xs text-[#6B7280]">Employee Identity</p>
                      <p className="text-lg font-semibold text-[#111827]">{record.employeeName}</p>
                      <p className="text-xs font-medium text-[#6B7280]">{record.designation || 'N/A'} • {record.employeeDepartment || 'N/A'}</p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <p className="text-xs text-[#6B7280]">Log Date</p>
                    <p className="text-lg font-semibold text-[#111827]">{formattedDate}</p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs text-[#6B7280]">Employment Status</p>
                    <div className="flex">
                      <span className="px-3 py-1 rounded-lg text-xs font-medium bg-[#F3F4F6] text-[#111827] capitalize">
                        {statusLabel}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#E5E7EB]">
                    <div className="space-y-1">
                      <p className="text-xs text-[#6B7280]">Gross Time</p>
                      <p className="text-lg font-semibold text-[#111827]">{grossHours}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-[#6B7280]">Total Steps</p>
                      <p className="text-lg font-semibold text-[#111827]">{(enhancedRecord.checkInPunches?.length || 0) + (enhancedRecord.checkOutPunches?.length || 0)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 border border-[#E5E7EB] rounded-2xl shadow-sm">
                <h4 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-4 border-b border-[#E5E7EB] pb-3">Standard Protocol</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#EFF6FF] text-[#0F2942] rounded-lg"><Clock className="w-4 h-4" /></div>
                      <span className="text-sm font-medium text-[#111827]">Shift Window</span>
                    </div>
                    <span className="text-sm font-semibold text-[#111827]">{shiftWindowLabel}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#FEF3C7] text-[#F59E0B] rounded-lg"><Zap className="w-4 h-4" /></div>
                      <span className="text-sm font-medium text-[#111827]">Late By</span>
                    </div>
                    <span className="text-sm font-semibold text-[#111827]">
                      {record.checkinLate && record.checkinLateTime && !isRecordFreeLate ? record.checkinLateTime : '00:00:00'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#FEE2E2] text-[#DC2626] rounded-lg"><LogOut className="w-4 h-4" /></div>
                      <span className="text-sm font-medium text-[#111827]">Early Checkout</span>
                    </div>
                    <span className="text-sm font-semibold text-[#111827]">
                      {record.checkoutEarly && record.checkoutEarlyTime ? record.checkoutEarlyTime : '00:00:00'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Punch History Timeline - 70% */}
            <div className="lg:col-span-7">
              <div className="bg-white p-4 sm:p-6 border border-[#E5E7EB] rounded-2xl shadow-sm h-full">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-[#111827]">Punch Timeline</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#0F2942]"></div>
                    <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Operational Sequence</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* In Punches */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#EFF6FF] text-[#0F2942] rounded-2xl flex items-center justify-center">
                        <Download className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-[#111827]">In-Flow Sessions</h4>
                        <p className="text-xs font-medium text-[#6B7280]">Start of active cycles</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {enhancedRecord.checkInPunches && enhancedRecord.checkInPunches.length > 0 ? (
                        [...enhancedRecord.checkInPunches].sort().map((time, idx) => (
                          <div key={`in-${idx}`} className="relative flex items-center justify-between p-4 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                            <span className="absolute left-4 top-0 bottom-0 w-px bg-[#E5E7EB]"></span>
                            <div className="relative flex items-center gap-3">
                              <span className="w-8 h-8 rounded-full border-2 border-[#16A34A] bg-white text-[#16A34A] text-xs font-semibold flex items-center justify-center">{idx + 1}</span>
                              <span className="text-sm font-semibold text-[#111827]">{formatTimeAMPM(time)}</span>
                            </div>
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#16A34A]">
                              <CheckCircle className="w-3.5 h-3.5" />
                              Verified
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="py-6 px-4 bg-[#F9FAFB] rounded-2xl border border-dashed border-[#E5E7EB] text-center">
                          <p className="text-sm font-medium text-[#6B7280]">No In-Flow Sessions</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Out Punches */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#EFF6FF] text-[#0F2942] rounded-2xl flex items-center justify-center">
                        <Upload className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-[#111827]">Out-Flow Sessions</h4>
                        <p className="text-xs font-medium text-[#6B7280]">Closure of active cycles</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {enhancedRecord.checkOutPunches && enhancedRecord.checkOutPunches.length > 0 ? (
                        [...enhancedRecord.checkOutPunches].sort().map((time, idx) => (
                          <div key={`out-${idx}`} className="relative flex items-center justify-between p-4 bg-[#F9FAFB] rounded-2xl border border-[#E5E7EB]">
                            <span className="absolute left-4 top-0 bottom-0 w-px bg-[#E5E7EB]"></span>
                            <div className="relative flex items-center gap-3">
                              <span className="w-8 h-8 rounded-full border-2 border-[#0F2942] bg-white text-[#0F2942] text-xs font-semibold flex items-center justify-center">{idx + 1}</span>
                              <span className="text-sm font-semibold text-[#111827]">{formatTimeAMPM(time)}</span>
                            </div>
                            <span className="text-xs font-medium text-[#0F2942]">Captured</span>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 px-4 bg-[#F9FAFB] rounded-2xl border border-dashed border-[#E5E7EB] flex flex-col items-center justify-center text-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#EFF6FF] text-[#0F2942] flex items-center justify-center">
                            <Upload className="w-4 h-4" />
                          </div>
                          <p className="text-sm font-medium text-[#6B7280]">No Out-Flow Sessions</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-[#E5E7EB] flex items-start gap-3">
                  <div className="p-2 bg-[#F3F4F6] rounded-lg">
                    <AlertCircle className="w-4 h-4 text-[#6B7280]" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#6B7280] leading-relaxed">
                      Total gross time is calculated based on the difference between the first check-in and the last check-out.
                      Discrepancies in punch sequences may affect the precision of automated analytics.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show details view if active
  if (showAttendanceDetails && selectedAttendanceRecord) {
    return renderAttendanceDetails();
  }

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2942] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#0F2942] text-white flex items-center justify-center shadow-md">
              <Clock className="w-6 h-6 stroke-[2]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0F2942] mb-0.5 leading-tight tracking-tight">My Attendance</h1>
              <p className="text-xs text-slate-500 font-medium">Employee self-service attendance & shift records</p>
            </div>
          </div>

          {/* Navigation Tabs (SaaS Segmented Pill Container) */}
          <div className="bg-slate-100/90 p-1.5 rounded-2xl border border-slate-200/80 shadow-2xs overflow-x-auto no-scrollbar">
            <nav className="flex space-x-1.5 min-w-max">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2.5 px-4 font-bold text-xs sm:text-sm flex items-center space-x-2 rounded-xl whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? 'bg-white text-blue-700 shadow-sm border border-slate-200/70 scale-[1.01]'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                    }`}
                  >
                    <IconComponent className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-slate-400'}`} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <main className="space-y-6">
          {activeTab === 'attendance' && renderAttendanceCalendar()}
          {activeTab === 'regularization' && renderRegularization()}
          {activeTab === 'holidays' && renderHolidayCalendar()}
          {activeTab === 'shift' && renderShiftRoster()}
        </main>

        {showDayView && renderDayView()}

        {/* Regularization Detail Modal */}
        {selectedRegRequest && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[100] !mt-0 p-4" onClick={() => setSelectedRegRequest(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className={`px-6 py-4 flex items-center justify-between ${selectedRegRequest.status === 'approved' ? 'bg-green-600' :
                selectedRegRequest.status === 'rejected' ? 'bg-red-600' : 'bg-indigo-600'
                } text-white`}>
                <div>
                  <h3 className="text-[18px] font-bold">Regularization Details</h3>
                  <p className="text-[12px] opacity-80 mt-0.5">{selectedRegRequest.requestId || 'Request'}</p>
                </div>
                <button onClick={() => setSelectedRegRequest(null)} className="p-2 rounded-lg bg-white/15 hover:bg-white/25 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
                {/* Status badge */}
                <div className="flex justify-end">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-[12px] font-bold capitalize ${getStatusColor(selectedRegRequest.status)}`}>
                    {selectedRegRequest.status?.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Date & Times */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[#F9FAFB] rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Date</p>
                    <p className="text-[14px] font-semibold text-[#111827]">
                      {selectedRegRequest.date ? formatDate(selectedRegRequest.date, { includeTime: false }) : '-'}
                    </p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Check In</p>
                    <p className="text-[14px] font-semibold text-[#111827]">{formatTimeAMPM(selectedRegRequest.checkIn) || '-'}</p>
                  </div>
                  <div className="bg-[#F9FAFB] rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Check Out</p>
                    <p className="text-[14px] font-semibold text-[#111827]">{formatTimeAMPM(selectedRegRequest.checkOut) || '-'}</p>
                  </div>
                </div>

                {/* Reason */}
                <div className="bg-[#F9FAFB] rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Reason</p>
                  <p className="text-[14px] text-[#111827] capitalize">{selectedRegRequest.reason || '-'}</p>
                </div>

                {/* Description */}
                {selectedRegRequest.description && (
                  <div className="bg-[#F9FAFB] rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Description</p>
                    <p className="text-[14px] text-[#111827] whitespace-pre-wrap">{selectedRegRequest.description}</p>
                  </div>
                )}

                {/* Comments / Rejection reason */}
                {(selectedRegRequest.comments || selectedRegRequest.rejectionReason) && (
                  <div className={`rounded-lg p-3 ${selectedRegRequest.status === 'rejected' ? 'bg-red-50 border border-red-100' : 'bg-green-50 border border-green-100'
                    }`}>
                    <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">
                      {selectedRegRequest.status === 'rejected' ? 'Rejection Reason' : 'Comments'}
                    </p>
                    <p className="text-[14px] text-[#111827] whitespace-pre-wrap first-letter:capitalize">
                      {selectedRegRequest.rejectionReason || selectedRegRequest.comments}
                    </p>
                  </div>
                )}

                {/* Approved / Rejected by */}
                {selectedRegRequest.approvedBy && (
                  <div className="bg-[#F9FAFB] rounded-lg p-3">
                    <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">
                      {selectedRegRequest.status === 'rejected' ? 'Rejected By' : 'Approved By'}
                    </p>
                    <p className="text-[14px] font-semibold text-[#111827]">{selectedRegRequest.approvedBy}</p>
                    {selectedRegRequest.approvedDate && (
                      <p className="text-[12px] text-[#6B7280] mt-0.5">
                        {formatDate(selectedRegRequest.approvedDate, { includeTime: true })}
                      </p>
                    )}
                  </div>
                )}

                {/* Submitted At */}
                {selectedRegRequest.submittedAt && (
                  <div className="text-[12px] text-[#9CA3AF] text-right">
                    Submitted: {formatDate(selectedRegRequest.submittedAt, { includeTime: true })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-[#E5E7EB] flex justify-end">
                <button
                  onClick={() => setSelectedRegRequest(null)}
                  className="px-5 py-2 bg-[#0F2942] hover:bg-[#0B2033] text-white text-[14px] font-semibold rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showRegularizationModal && renderRegularizationModal()}

        {/* New Regularization Request Modal (reusable component, employee locked to self) */}
        <RegularizationRequestModal
          isOpen={showNewRegModal}
          onClose={() => {
            setShowNewRegModal(false);
            setRegModalPrefilledData(null);
          }}
          prefilledData={employeeProfile ? {
            employeeId: employeeProfile._id || employeeProfile.id,
            employeeCode: employeeProfile.employeeId,
            employeeName: `${employeeProfile.personalInfo?.firstName || ''} ${employeeProfile.personalInfo?.lastName || ''}`.trim(),
            employeeDepartment: employeeProfile.employmentInfo?.department || '',
            employeeData: employeeProfile,
            ...(regModalPrefilledData || {}),
          } : null}
          disableEmployeeSelect={true}
          onSuccess={() => {
            setShowNewRegModal(false);
            setRegModalPrefilledData(null);
            if (employeeId) {
              loadRegularizationRequests(regularizationPage, regularizationPageSize);
            }
          }}
        />

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          loading={exportLoading}
          title="Export Attendance"
          showDateRange
          scopedFormatExport={{
            onExport: handleMyAttendanceScopedExport,
            formats: ['csv', 'xlsx', 'pdf'],
            showCurrentPage: true,
            showFiltered: true,
            showDateRange: true,
            showAll: true,
          }}
        />
      </div>
    </div>
  );
};

export default MyAttendance;
